import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";

import AccordionComponent from "../../../../../common/Accordion";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartExportButton from "../../../../../common/HighchartExportButton";

import { getRORWorkBreakDown } from "../../../../../services/RORDashboardAPI";

interface RORWorkloadBreakdownProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const STATUS_LABELS: Record<string, string> = {
  Overdue: "Overdue",
  InReview: "In Review",
  Exception: "Exception",
  DraftReview: "Preliminary Review",
  NotInitiated: "Not Initiated",
  ReviewCompleted: "Review Completed",
};

const STATUS_COLORS: Record<string, string> = {
  Overdue: "#ff9f43",

  InReview: "#60a5fa",
  DraftReview: "#a1a1aa",
  ReviewCompleted: "#22c55e",
  NotInitiated: "#d1d5db",
  Exception: "#6b7280",
};

const RORWorkloadBreakdown: React.FC<RORWorkloadBreakdownProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [summaryData, setSummaryData] = useState<any[]>([]);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const fetchWorkloadData = async () => {
    try {
      const result = await getRORWorkBreakDown(1);
      setSummaryData(result);
    } catch (error) {
      console.error("Failed to load case summary data", error);
    }
  };

  useEffect(() => {
    fetchWorkloadData();
  }, []);

  const normalizedData = summaryData.map((item: any) => ({
    processName: item.processName,
    statusCounts: JSON.parse(item.statusCounts),
  }));

  const buildDonutChartOptions = (process: any): Highcharts.Options => {
    const statusCounts = process.statusCounts as Record<string, number>;

    const data = Object.keys(STATUS_COLORS).map((status) => ({
      name: STATUS_LABELS[status] || status,
      y: statusCounts[status] ?? 0,
      color: STATUS_COLORS[status],
    }));

    return {
      chart: {
        type: "pie",
        height: 350,
      },

      title: {
        text: undefined,
        verticalAlign: "middle",
        floating: true,
        style: {
          fontSize: "16px",
          fontWeight: "bold",
        },
      },

      plotOptions: {
        pie: {
          innerSize: "60%", // donut
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.Point) {
              const point = this as Highcharts.Point;

              return point.y && point.y > 0 ? `${point.name} ${point.y}` : null;
            },
          },
          showInLegend: true, // show even zero in legend
        },
      },

      tooltip: {
        pointFormat: "<b>{point.name}</b>: {point.y}",
      },

      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },

      series: [
        {
          type: "pie",
          name: "Count",
          data,
        },
      ],

      credits: { enabled: false },
    };
  };

  const [caseSummaryKey, setCaseSummaryKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (caseSummaryKey === key) {
      return; // do nothing
    }
    setCaseSummaryKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={caseSummaryKey}
      onSelect={handleBodyAccordionSelect}
      isDashboard={true}
    />
  );

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const hasNoData =
    normalizedData.length === 0 ||
    normalizedData.every((process) =>
      Object.values(process.statusCounts).every((count) => Number(count) === 0),
    );
  return (
    <div className="bg-white rounded-lg shadow-md w-100 h-100">
      {summaryAccordion([
        {
          title: (
            <div
              className="d-flex  align-items-center w-100 h-100 "
              onClick={(e) => e.stopPropagation()}
            >
              <span className="me-auto">Workload Breakdown</span>
              <HighchartExportButton chartRef={chartComponentRef} />
            </div>
          ),
          content: (
            <>
              {hasNoData ? (
                <div className="text-center">
                  {/* <img src="/noChart.png" alt="noChart" className="nodata" /> */}
                  <div className="nodata_wrapper">
                    <span className="material-symbols-outlined">
                      bar_chart_off
                    </span>{" "}
                    No Data Available
                  </div>
                </div>
              ) : (
                normalizedData.map((process, index) => (
                  <HighchartsReact
                    key={index}
                    highcharts={Highcharts}
                    options={buildDonutChartOptions(process)}
                    ref={chartComponentRef}
                  />
                ))
              )}
            </>
          ),
        },
      ])}
    </div>
  );
};

export default RORWorkloadBreakdown;
